import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  Mail,
  MapPin,
  Plus,
  X,
  User,
  Send,
  MessageCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";
import styles from "../styles/Contacts.module.scss";

interface Contact {
  id: string;
  name: string;
  role: string;
  phone: string;
  email: string;
  location?: string;
  avatar?: string;
}

const Contacts: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([
    {
      id: "1",
      name: "Dr. Sarah Martinez",
      role: "Marine Biologist",
      phone: "+1 (555) 123-4567",
      email: "sarah.martinez@oceanguard.org",
      location: "Pacific Research Station",
    },
    {
      id: "2",
      name: "Captain James Wilson",
      role: "Coast Guard Commander",
      phone: "+1 (555) 987-6543",
      email: "j.wilson@coastguard.gov",
      location: "Harbor Command Center",
    },
    {
      id: "3",
      name: "Dr. Elena Rodriguez",
      role: "Environmental Scientist",
      phone: "+1 (555) 456-7890",
      email: "elena.rodriguez@marine.edu",
      location: "Marine Research Institute",
    },
    {
      id: "4",
      name: "Mike Thompson",
      role: "Emergency Response Coordinator",
      phone: "+1 (555) 321-0987",
      email: "mthompson@emergency.gov",
      location: "Emergency Operations Center",
    },
  ]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newContact, setNewContact] = useState({
    name: "",
    role: "",
    phone: "",
    email: "",
    location: "",
  });

  const handleAddContact = () => {
    if (
      !newContact.name ||
      !newContact.role ||
      !newContact.phone ||
      !newContact.email
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    const contact: Contact = {
      id: Date.now().toString(),
      ...newContact,
    };

    setContacts([...contacts, contact]);
    setNewContact({ name: "", role: "", phone: "", email: "", location: "" });
    setIsModalOpen(false);
    toast.success("Contact added successfully!");
  };

  const { currentUser } = useAuth();
  const [messageForm, setMessageForm] = useState({
    subject: "",
    message: "",
    category: "general-inquiry",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);

  const handleSubmitMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser) {
      toast.error("Please log in to send a message");
      return;
    }

    if (!messageForm.subject.trim() || !messageForm.message.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const contactData = {
        userName:
          currentUser.displayName ||
          currentUser.email?.split("@")[0] ||
          "Anonymous",
        userEmail: currentUser.email || "",
        userPhone: "", // Could be added to form if needed
        subject: messageForm.subject,
        message: messageForm.message,
        category: messageForm.category,
      };

      await apiService.createContact(contactData);

      toast.success("Message sent successfully!");
      setMessageForm({
        subject: "",
        message: "",
        category: "general-inquiry",
      });
      setIsMessageModalOpen(false);
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCall = (phone: string) => {
    window.open(`tel:${phone}`);
  };

  const handleEmail = (email: string) => {
    window.open(`mailto:${email}`);
  };

  return (
    <div className={styles.contactsPage}>
      <div className={styles.container}>
        {/* Header */}
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className={styles.title}>Emergency Contacts</h1>
          <p className={styles.subtitle}>
            Connect with marine safety experts and emergency responders
          </p>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          className={styles.actionButtonsContainer}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <button
            className={styles.addButton}
            onClick={() => setIsModalOpen(true)}
          >
            <Plus size={20} />
            Add Contact
          </button>
          <button
            className={styles.messageButton}
            onClick={() => setIsMessageModalOpen(true)}
          >
            <MessageCircle size={20} />
            Send Message
          </button>
        </motion.div>

        {/* Contacts Grid */}
        <motion.div
          className={styles.contactsGrid}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {contacts.map((contact, index) => (
            <motion.div
              key={contact.id}
              className={styles.contactCard}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -5, scale: 1.02 }}
            >
              <div className={styles.contactHeader}>
                <div className={styles.avatar}>
                  {contact.avatar ? (
                    <img src={contact.avatar} alt={contact.name} />
                  ) : (
                    <User size={24} />
                  )}
                </div>
                <div className={styles.contactInfo}>
                  <h3 className={styles.contactName}>{contact.name}</h3>
                  <p className={styles.contactRole}>{contact.role}</p>
                </div>
              </div>

              <div className={styles.contactDetails}>
                <div className={styles.contactItem}>
                  <Phone size={16} />
                  <span>{contact.phone}</span>
                </div>
                <div className={styles.contactItem}>
                  <Mail size={16} />
                  <span>{contact.email}</span>
                </div>
                {contact.location && (
                  <div className={styles.contactItem}>
                    <MapPin size={16} />
                    <span>{contact.location}</span>
                  </div>
                )}
              </div>

              <div className={styles.contactActions}>
                <button
                  className={styles.actionButton}
                  onClick={() => handleCall(contact.phone)}
                >
                  <Phone size={16} />
                  Call
                </button>
                <button
                  className={styles.actionButton}
                  onClick={() => handleEmail(contact.email)}
                >
                  <Mail size={16} />
                  Email
                </button>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Add Contact Modal */}
        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              className={styles.modalOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
            >
              <motion.div
                className={styles.modal}
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={styles.modalHeader}>
                  <h2>Add New Contact</h2>
                  <button
                    className={styles.closeButton}
                    onClick={() => setIsModalOpen(false)}
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className={styles.modalContent}>
                  <div className={styles.formGroup}>
                    <label>Name *</label>
                    <input
                      type="text"
                      value={newContact.name}
                      onChange={(e) =>
                        setNewContact({ ...newContact, name: e.target.value })
                      }
                      placeholder="Enter full name"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label>Role *</label>
                    <input
                      type="text"
                      value={newContact.role}
                      onChange={(e) =>
                        setNewContact({ ...newContact, role: e.target.value })
                      }
                      placeholder="e.g., Marine Biologist, Coast Guard Officer"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label>Phone *</label>
                    <input
                      type="tel"
                      value={newContact.phone}
                      onChange={(e) =>
                        setNewContact({ ...newContact, phone: e.target.value })
                      }
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label>Email *</label>
                    <input
                      type="email"
                      value={newContact.email}
                      onChange={(e) =>
                        setNewContact({ ...newContact, email: e.target.value })
                      }
                      placeholder="contact@example.com"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label>Location</label>
                    <input
                      type="text"
                      value={newContact.location}
                      onChange={(e) =>
                        setNewContact({
                          ...newContact,
                          location: e.target.value,
                        })
                      }
                      placeholder="Office or station location"
                    />
                  </div>
                </div>

                <div className={styles.modalActions}>
                  <button
                    className={styles.cancelButton}
                    onClick={() => setIsModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className={styles.submitButton}
                    onClick={handleAddContact}
                  >
                    Add Contact
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Message Modal */}
        <AnimatePresence>
          {isMessageModalOpen && (
            <motion.div
              className={styles.modalOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMessageModalOpen(false)}
            >
              <motion.div
                className={styles.modal}
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={styles.modalHeader}>
                  <h2>Send Message</h2>
                  <button
                    className={styles.closeButton}
                    onClick={() => setIsMessageModalOpen(false)}
                  >
                    <X size={20} />
                  </button>
                </div>

                <form
                  onSubmit={handleSubmitMessage}
                  className={styles.modalContent}
                >
                  <div className={styles.formGroup}>
                    <label>Category</label>
                    <select
                      value={messageForm.category}
                      onChange={(e) =>
                        setMessageForm({
                          ...messageForm,
                          category: e.target.value,
                        })
                      }
                      className={styles.select}
                    >
                      <option value="general-inquiry">General Inquiry</option>
                      <option value="emergency">Emergency</option>
                      <option value="report-issue">Report Issue</option>
                      <option value="feedback">Feedback</option>
                      <option value="technical-support">
                        Technical Support
                      </option>
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label>Subject *</label>
                    <input
                      type="text"
                      value={messageForm.subject}
                      onChange={(e) =>
                        setMessageForm({
                          ...messageForm,
                          subject: e.target.value,
                        })
                      }
                      placeholder="Brief description of your message"
                      required
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label>Message *</label>
                    <textarea
                      value={messageForm.message}
                      onChange={(e) =>
                        setMessageForm({
                          ...messageForm,
                          message: e.target.value,
                        })
                      }
                      placeholder="Describe your inquiry, issue, or feedback in detail..."
                      rows={6}
                      required
                    />
                  </div>

                  <div className={styles.modalActions}>
                    <button
                      type="button"
                      className={styles.cancelButton}
                      onClick={() => setIsMessageModalOpen(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={styles.submitButton}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <div className={styles.spinner} />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={16} />
                          Send Message
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Contacts;
